/**
 * Irregularity Reason Translation Table
 * ======================================
 * Maps internal/technical reason codes → customer-facing descriptions used
 * in delay and cancellation declarations (PT + EN).
 *
 * MAINTENANCE: Edit only this file to change any mapping.
 * Each entry supports multiple aliases so the table stays correct even if the
 * flight system sends numeric IATA codes, legacy word codes, or variant spellings.
 *
 * The customer-facing PDF text (textPT/textEN) collapses every code into one
 * of 4 declaration reason groups: Weather, Technical, Operational, or
 * External Circumstances. Operator-facing labels (labelPT/labelEN) remain
 * distinct per code so the selection UI keeps its full granularity.
 *
 * Fallback: any unrecognised or null code defaults to "razões operacionais".
 */

export interface IrregularityReason {
  /** Canonical code shown in the operator UI */
  code: string;
  /** Internal label shown to the operator (Portuguese) */
  labelPT: string;
  /** Internal label shown to the operator (English) */
  labelEN: string;
  /** Customer-facing reason phrase for the declaration (Portuguese) */
  textPT: string;
  /** Customer-facing reason phrase for the declaration (English) */
  textEN: string;
}

interface ReasonTableEntry extends IrregularityReason {
  /** Every code/alias that resolves to this entry (matched case-insensitively) */
  aliases: string[];
}

// ---------------------------------------------------------------------------
// Translation table
// Numeric codes follow the IATA standard delay code list.
// ---------------------------------------------------------------------------
const REASON_TABLE: ReasonTableEntry[] = [
  // -------------------------------------------------------------------------
  // WEATHER
  // IATA categories: WEATHER (71–77)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'MET', 'WX', 'METEO', 'WEATHER', 'W',
      '71',                        // Departure Station Weather
      '72',                        // Destination Station Weather
      '73',                        // En Route or Alternate Weather
      '75', '75A', '75B', '75C',   // De-Icing of Aircraft
      '76',                        // Removal of Snow/Ice/Water/Sand
      '77',                        // Ground Handling Impaired by Adverse Weather
    ],
    code: 'MET',
    labelPT: 'Meteorologia',
    labelEN: 'Weather',
    textPT: 'condições meteorológicas adversas',
    textEN: 'adverse weather conditions',
  },

  // -------------------------------------------------------------------------
  // TECHNICAL
  // IATA categories: TECHNICAL AND AIRCRAFT EQUIPMENT (41–49),
  //                  DAMAGE TO AIRCRAFT (51–52),
  //                  EDP / AUTOMATED EQUIPMENT FAILURE (55–58)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'MTECH', 'TEC', 'TECH', 'TECHNICAL', 'TC', 'T',
      'TE',  // Technical Extraordinary (bird strike, lightning, GSE damage)
      'TH',  // Technical Hard Landing
      // Technical and Aircraft Equipment (41–49)
      '41A', '41B', '41C',                     // Aircraft Defects
      '42A', '42B', '42C', '42D',              // Late Release / Maintenance
      '43A', '43B', '43C', '43D', '43F',       // Non-Scheduled Works (43E -> external circumstances)
      '44A', '44B',                            // Lack/Breakdown of Spares or Equipment
      '45',                                    // AOG Spares
      '46A', '46B', '46C',                     // Aircraft Change due to Defects
      '47',                                    // Lack of Planned Standby Aircraft
      '48',                                    // Scheduled Cabin Configuration Adjustment
      '49',                                    // Operational Impact due to A/C deviation
      // Damage to Aircraft (51–52)
      '51B', '51C', '51D', '51E', '51F', '51G', // (51A -> external circumstances)
      '52A', '52B', '52C', '52D', '52E',
      // EDP / Automated Equipment Failure (55–58)
      '55A', '55B', '55C', '55D', '55E', '55F', '55G',
      '56', '57',
      '58A', '58B', '58C', '58D', '58E', '58F',
    ],
    code: 'MTECH',
    labelPT: 'Razões Técnicas',
    labelEN: 'Technical Reasons',
    textPT: 'razões técnicas',
    textEN: 'technical reasons',
  },

  // -------------------------------------------------------------------------
  // OPERATIONAL (catch-all for airline/handling/crew/reactionary causes)
  // IATA categories: AIRLINES INTERNAL CODES (2–4D), OTHERS (6–9),
  //                  PASSENGER AND BAGGAGE (11–19), CARGO AND MAIL (21–26),
  //                  MAIL ONLY (27–29), AIRCRAFT AND RAMP HANDLING (31–39),
  //                  FLIGHT OPERATIONS AND CREWING (61–69), REACTIONARY (91–96)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'OP', 'OPER', 'OPERATIONAL', 'O',
      'OE',  // Operational Extraordinary (strikes, NOTAM)
      // Airlines Internal Codes (2–4D)
      '2', '3', '4', '4A', '4B', '4C', '4D',
      // Others (6–9)
      '6', '8A', '8B', '8C', '9',
      // Passenger and Baggage (11–19)
      '11', '12', '13A', '13B',
      '14A', '14B', '14C',
      '15A', '15B', '15C', '15D', '15E', '15F',
      '16A', '16B', '16C', '16D', '16E', '16F', '16G', '16H', '16I', '16J',
      '17A', '17B',
      '18A', '18B', '18C', '18D', '18E',
      '19A', '19B', '19C', '19D', '19E', '19F', '19G',
      // Cargo and Mail (21–26)
      '21A', '21B', '21C', '21D',
      '22A', '22B',
      '23A', '23B', '23C', '23D', '23E',
      '24A', '24B', '25',
      '26A', '26B', '26C', '26D', '26E',
      // Mail Only (27–29)
      '27A', '27B', '27C', '28', '29',
      // Aircraft and Ramp Handling (31–39)
      '31A', '31B', '31C', '31D', '31E',
      '32A', '32B', '32C', '32D', '32E', '32F', '32G',
      '33',
      '34A', '34B', '34C', '34D', '34E', '34F',
      '35A', '35B', '35C', '35D', '35E',
      '36A', '36B', '36C',
      '37A', '37B', '37C', '37D', '37E', '37F', '37G',
      '38',
      '39A', '39B', '39C', '39D',
      // Flight Operations and Crewing (61–69)
      '61A', '61B', '61C', '61D',
      '62A', '62B', '62C',
      '63A', '63B', '63C', '63D', '63E', '63F', '63G', '63H', '63I',
      '64A', '64B', '64C',
      '65A', '65B',
      '66A', '66B', '66C', '66D', '66E', '66F',
      '67A', '67B', '67C',
      '68A', '68B', '68C', '68D', '68E',
      '69',
      // Reactionary (91–96)
      '91',
      '92A', '92B', '92C', '92D',
      '93', '94',
      '95A', '95B',
      '96A', '96B', '96C',
    ],
    code: 'OP',
    labelPT: 'Motivos Operacionais',
    labelEN: 'Operational Reasons',
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },

  // -------------------------------------------------------------------------
  // AIR TRAFFIC RESTRICTIONS
  // IATA category: ATFM RESTRICTIONS (81–84)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'ATC',
      '81A', '81B', '81C',         // ATFM Weather / High Demand / ATC Routing
      '82A', '82B', '82C',         // ATFM Staff Shortage / Equipment / Industrial Action (ATC)
      '83A', '83B', '83C', '83D', '83E', // ATFM Airport Demand / Noise / Staff / Equipment / IA
      '84',                        // ATFM Weather at Destination
      '98',                        // Industrial Action Outside Own Airline
      '43E',                       // Non-Scheduled Works (extracted from Technical)
      '51A',                       // Damage to Aircraft (extracted from Technical)
    ],
    code: 'ATC',
    labelPT: 'Restrições de Tráfego Aéreo (ATC)',
    labelEN: 'Air Traffic Restrictions (ATC)',
    // Mapped to the "external circumstances" PDF group
    textPT: 'circunstâncias externas',
    textEN: 'external circumstances',
  },

  // -------------------------------------------------------------------------
  // SECURITY
  // IATA category: AIRPORT AND GOVERNMENTAL AUTHORITIES — security checks (85*)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'SEC', 'SECURITY',
      '85A', '85B', '85C', '85D',  // Checkpoint / X-Ray screening
      '85E',                       // Unruly Passenger
      '85F', '85G', '85H', '85I', '85J', // Profiling / Aircraft Search / Security Breach
    ],
    code: 'SEC',
    labelPT: 'Questões de Segurança',
    labelEN: 'Security Issues',
    // Mapped to the "external circumstances" PDF group
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },

  // -------------------------------------------------------------------------
  // AIRPORT OPERATIONS
  // IATA category: AIRPORT AND GOVERNMENTAL AUTHORITIES — authorities/infra (86–89)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'APT', 'AIRPORT',
      '86A', '86B', '86C', '86D', '86E', '86F', '86G', '86H', // Gov./Customs/Immigration
      '87A', '87B', '87C', '87D', '87E', '87F', '87G',        // Airport Infrastructure
      '87H', '87I', '87J', '87K', '87L', '87M', '87N',
      '88',                        // Restrictions at Airport of Destination
      '89',                        // Restrictions at Airport of Departure
    ],
    code: 'APT',
    labelPT: 'Operações Aeroportuárias',
    labelEN: 'Airport Operations',
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },

  // -------------------------------------------------------------------------
  // COMMERCIAL
  // No dedicated IATA numeric code — internal use only
  // -------------------------------------------------------------------------
  {
    aliases: [
      'COM', 'COMMERCIAL', 'C',
      'CM',  // Commercial Reasons
      'DC',  // Descomercializado (flight withdrawn from sale)
    ],
    code: 'COM',
    labelPT: 'Comerciais',
    labelEN: 'Commercial',
    // Not communicated directly to the customer; mapped to generic operational
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },

  // -------------------------------------------------------------------------
  // STRIKE
  // IATA category: MISCELLANEOUS (97–98)
  // -------------------------------------------------------------------------
  {
    aliases: [
      'STK', 'GRV', 'STRIKE',
      '97',  // Industrial Action Within Own Airline (98 -> external circumstances)
    ],
    code: 'STK',
    labelPT: 'Greve',
    labelEN: 'Strike',
    // TODO: confirm internally — may change to explicit strike wording for transparency
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },

  // -------------------------------------------------------------------------
  // OTHER / CATCH-ALL
  // IATA: 99 (Unmatched Reason Code) and any other unlisted codes
  // -------------------------------------------------------------------------
  {
    aliases: [
      '0TH', 'OTH', 'OTHER',
      '99',  // Unmatched Reason Code
    ],
    code: '0TH',
    labelPT: 'Outras Circunstâncias',
    labelEN: 'Other Circumstances',
    textPT: 'razões operacionais',
    textEN: 'operational reasons',
  },
];

// Fallback for unknown / absent codes (also used if code = null)
const FALLBACK_TEXT = {
  textPT: 'razões operacionais',
  textEN: 'operational reasons',
} as const;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Full ordered list of selectable reasons for operator dropdowns / multi-select lists */
export const IRREGULARITY_REASON_LIST: IrregularityReason[] = REASON_TABLE.map(
  ({aliases: _a, ...rest}) => rest
);

/**
 * Find the canonical IrregularityReason for any code, including numeric IATA codes and aliases.
 * Returns null if no match is found.
 */
export const findCanonicalReason = (code: string): IrregularityReason | null => {
  const upper = code.trim().toUpperCase();

  // 1. Exact match (covers all explicit aliases and word codes)
  const exact = REASON_TABLE.find((e) => e.aliases.includes(upper));
  if (exact) {
    const {aliases: _a, ...rest} = exact;
    return rest;
  }

  // 2. Prefix match: bare numeric code (e.g. "41") → find first alias like "41A", "41B"
  //    Only applies when the incoming code is purely digits to avoid false positives
  //    (e.g. code "4" must not match "41A").
  if (/^\d+$/.test(upper)) {
    const prefix = REASON_TABLE.find((e) =>
      e.aliases.some((a) => new RegExp(`^${upper}[A-Z]`).test(a))
    );
    if (prefix) {
      const {aliases: _a, ...rest} = prefix;
      return rest;
    }
  }

  return null;
};

/**
 * Resolve customer-facing text for any internal or IATA code.
 * Falls back to "motivos operacionais" for unknown / null / undefined codes.
 */
export const resolveIrregularityText = (
  code: string | null | undefined
): {textPT: string; textEN: string} =>
  !code ? FALLBACK_TEXT : (findCanonicalReason(code) ?? FALLBACK_TEXT);

// ---------------------------------------------------------------------------
// Standard declaration sentence builders
// Ensures no internal codes or technical jargon appear in the final document.
// ---------------------------------------------------------------------------

/**
 * Builds the full bilingual sentence for a delay declaration.
 * e.g. PT: "O voo chegou ao destino com atraso devido a motivos técnicos."
 *      EN: "The flight arrived at the destination with a delay due to technical reasons."
 */
export const buildDelayText = (reason: Pick<IrregularityReason, 'textPT' | 'textEN'>) => ({
  pt: `O voo chegou ao destino com atraso devido a ${reason.textPT}.`,
  en: `The flight arrived at the destination with a delay due to ${reason.textEN}.`,
});

/**
 * Builds the full bilingual sentence for a cancellation declaration.
 * e.g. PT: "O passageiro não viajou no voo S4201 devido a motivos técnicos."
 *      EN: "The passenger did not travel on flight S4201 due to technical reasons."
 */
export const buildCancellationText = (
  reason: Pick<IrregularityReason, 'textPT' | 'textEN'>,
  flightLabel?: string
) => ({
  pt: `O passageiro não viajou no voo${flightLabel ? ` ${flightLabel}` : ''} devido a ${reason.textPT}.`,
  en: `The passenger did not travel on flight${flightLabel ? ` ${flightLabel}` : ''} due to ${reason.textEN}.`,
});
